// Types for the gradient-training telemetry endpoints.
// GET /api/v1/gradient/status  → GradientStatus (cheap liveness poll)
// GET /api/v1/gradient         → GradientTelemetry (the full blob; GENERIC schema —
//                                render whatever keys are present).

export interface GradientStatus {
  present: boolean;
  live: boolean;        // a gradient run is actively writing telemetry
  age_s?: number;
  task?: string;
  step?: number;
  total?: number;
  status?: string;
}

export interface GradientMeta {
  task: string;
  status: "running" | "done";
  step: number;
  total: number;
  loss: number;
  elapsed_s: number;
  metrics: Record<string, number>;
}

// One point of the training curve. Always has step + loss; the extra metric
// columns (e.g. peak_mean) vary by task and are discovered from the row keys.
export interface GradientCurveRow {
  step: number;
  loss: number;
  [metricName: string]: number;
}

// ‖∂loss/∂θ‖ per parameter block — "where learning flows".
export interface GradientBlock {
  name: string;
  lo: number;
  hi: number;
  norm: number;
}

// A 2D loss surface sampled around the current weights (grid centre = now).
export interface GradientLandscape {
  z: number[][];
  n: number;
  loss_center: number;
  grad_norm: number;
  span: number;
}

// One rendered tick of "what the retina sees". `frame` (grayscale) and `sal`
// (retina saliency) are row-major arrays of length h*w (res = [h, w]), values
// ~0..1+. The bearings are in [-1,1] with the retina convention u=x, v=y and
// v=-1 the TOP row: x = (u+1)/2·w, y = (v+1)/2·h.
export interface PhenotypeTick {
  frame: number[];
  sal: number[];
  near: [number, number];
  next: [number, number];
  true: [number, number];
  vis: number;
}

// One phenotype clip: a fixed retina resolution plus a sequence of ticks.
export interface PhenotypeClip {
  res: [number, number]; // [h, w]
  ticks: PhenotypeTick[];
}

export type GradientPhenotype = PhenotypeClip[];

export interface GradientTelemetry {
  meta: GradientMeta;
  curve: GradientCurveRow[];
  blocks?: GradientBlock[];
  landscape?: GradientLandscape;
  phenotype?: GradientPhenotype;
}

// The metric column names present in a curve (everything beyond step/loss),
// discovered in first-seen order across all rows.
export function curveMetricKeys(curve: GradientCurveRow[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of curve) {
    for (const k of Object.keys(row)) {
      if (k === "step" || k === "loss") continue;
      if (typeof row[k] !== "number" || !Number.isFinite(row[k])) continue;
      if (!seen.has(k)) { seen.add(k); out.push(k); }
    }
  }
  return out;
}

// Count of phenotype clips.
export function phenotypeCount(p: GradientPhenotype | null | undefined): number | null {
  return Array.isArray(p) ? p.length : null;
}
